/**
 * Portal API helpers — company portal pages, employee management,
 * course assignments, and department access codes.
 */

import { supabase } from "./supabase";

export interface PortalData {
  id: string;
  name: string;
  type: string;
  industry: string;
  logo_url: string | null;
  portal_primary_color: string | null;
  portal_tagline: string | null;
  ein: string | null;
  portal_slug: string | null;
  industry_preferences: string[] | null;
  portal_link_visible: boolean | null;
}

export interface PortalEmployee {
  id: string;
  name: string;
  email: string;
  role: string;
  department_id: string | null;
  department_name: string | null;
  approval_status: string;
  completed_certifications: string[];
  active_licenses: string[];
  created_at: string;
}

export interface PortalDepartment {
  id: string;
  name: string;
  budget: number | null;
  access_code: string | null;
  access_code_generated_at: string | null;
  employee_count: number;
  description: string | null;
  admin_user_id: string | null;
  admin_name: string | null;
  is_active: boolean | null;
}

export interface CourseAssignment {
  id: string;
  organization_id: string;
  department_id: string | null;
  user_id: string | null;
  program_id: string;
  assigned_by: string;
  status: string;
  due_date: string | null;
  created_at: string;
  user_name?: string;
  user_email?: string;
  department_name?: string;
}

export interface PortalInvite {
  id: string;
  token: string;
  email: string;
  organization_id: string;
  department_id: string | null;
  department_name: string | null;
  invited_by: string;
  inviter_name: string | null;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface PortalOrder {
  id: string;
  organization_id: string | null;
  department_id: string | null;
  user_id: string | null;
  order_type: string;
  item_name: string;
  item_id: string | null;
  quantity: number;
  amount: number;
  currency: string;
  status: string;
  receipt_url: string | null;
  invoice_url: string | null;
  created_at: string;
}

export async function fetchPortalBySlug(slug: string): Promise<PortalData | null> {
  const { data, error } = await supabase.rpc("get_portal_by_slug", { p_slug: slug });
  if (error || !data) return null;
  return data as PortalData;
}

export async function fetchPortalEmployees(orgId: string): Promise<PortalEmployee[]> {
  const { data, error } = await supabase
    .from("sentinel_users")
    .select(`
      id, name, email, role, department_id, approval_status,
      completed_certifications, active_licenses, created_at
    `)
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false });

  if (error || !data) return [];

  const deptIds = [...new Set(data.map((u) => u.department_id).filter(Boolean))] as string[];
  let deptMap: Record<string, string> = {};
  if (deptIds.length > 0) {
    const { data: depts } = await supabase
      .from("sentinel_departments")
      .select("id, name")
      .in("id", deptIds);
    if (depts) {
      deptMap = Object.fromEntries(depts.map((d) => [d.id, d.name]));
    }
  }

  return data.map((u) => ({
    ...u,
    department_name: u.department_id ? (deptMap[u.department_id] ?? null) : null,
  })) as PortalEmployee[];
}

export async function fetchPortalDepartments(orgId: string): Promise<PortalDepartment[]> {
  const { data: depts, error } = await supabase
    .from("sentinel_departments")
    .select("id, name, budget, access_code, access_code_generated_at, description, admin_user_id, is_active")
    .eq("organization_id", orgId)
    .order("name", { ascending: true });

  if (error || !depts) return [];

  const { data: users } = await supabase
    .from("sentinel_users")
    .select("department_id")
    .eq("organization_id", orgId);

  const countMap: Record<string, number> = {};
  for (const u of users ?? []) {
    if (u.department_id) {
      countMap[u.department_id] = (countMap[u.department_id] ?? 0) + 1;
    }
  }

  const adminIds = [...new Set(depts.map((d) => d.admin_user_id).filter(Boolean))] as string[];
  let adminMap: Record<string, string> = {};
  if (adminIds.length > 0) {
    const { data: adminUsers } = await supabase
      .from("sentinel_users")
      .select("id, name")
      .in("id", adminIds);
    if (adminUsers) {
      adminMap = Object.fromEntries(adminUsers.map((u) => [u.id, u.name]));
    }
  }

  return depts.map((d) => ({
    ...d,
    employee_count: countMap[d.id] ?? 0,
    admin_name: d.admin_user_id ? (adminMap[d.admin_user_id] ?? null) : null,
  })) as PortalDepartment[];
}

export async function generateDeptAccessCode(deptId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("generate_dept_access_code", { p_dept_id: deptId });
  if (error) return null;
  return data as string;
}

export async function approveEmployee(userId: string, approved: boolean): Promise<string | null> {
  const { data, error } = await supabase.rpc("approve_employee", {
    p_user_id: userId,
    p_approved: approved,
  });
  if (error) return null;
  return data as string;
}

export async function validateDeptAccessCode(
  accessCode: string,
): Promise<{ organization_id: string; organization_name: string; department_id: string; department_name: string } | null> {
  const { data, error } = await supabase.rpc("validate_dept_access_code", {
    p_access_code: accessCode,
  });
  if (error || !data || data.length === 0) return null;
  const row = data[0];
  return {
    organization_id: row.organization_id,
    organization_name: row.organization_name,
    department_id: row.department_id,
    department_name: row.department_name,
  };
}

export async function registerEmployeeByAccessCode(
  accessCode: string,
  authId: string,
  email: string,
  fullName: string,
): Promise<{ organization_id: string; department_id: string; department_name: string; organization_name: string } | null> {
  const { data, error } = await supabase.rpc("register_employee_by_access_code", {
    p_access_code: accessCode,
    p_auth_id: authId,
    p_email: email,
    p_full_name: fullName,
  });
  if (error || !data || data.length === 0) return null;
  const row = data[0];
  return {
    organization_id: row.organization_id,
    department_id: row.department_id,
    department_name: row.department_name,
    organization_name: row.organization_name,
  };
}

export async function fetchCourseAssignments(orgId: string): Promise<CourseAssignment[]> {
  const { data, error } = await supabase
    .from("sentinel_course_assignments")
    .select(`
      id, organization_id, department_id, user_id, program_id,
      assigned_by, status, due_date, created_at
    `)
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false });

  if (error || !data) return [];

  const userIds = [...new Set(data.map((a) => a.user_id).filter(Boolean))] as string[];
  let userMap: Record<string, { name: string; email: string }> = {};
  if (userIds.length > 0) {
    const { data: users } = await supabase
      .from("sentinel_users")
      .select("id, name, email")
      .in("id", userIds);
    if (users) {
      userMap = Object.fromEntries(users.map((u) => [u.id, { name: u.name, email: u.email }]));
    }
  }

  const deptIds = [...new Set(data.map((a) => a.department_id).filter(Boolean))] as string[];
  let deptMap: Record<string, string> = {};
  if (deptIds.length > 0) {
    const { data: depts } = await supabase
      .from("sentinel_departments")
      .select("id, name")
      .in("id", deptIds);
    if (depts) {
      deptMap = Object.fromEntries(depts.map((d) => [d.id, d.name]));
    }
  }

  return data.map((a) => ({
    ...a,
    user_name: a.user_id ? userMap[a.user_id]?.name : undefined,
    user_email: a.user_id ? userMap[a.user_id]?.email : undefined,
    department_name: a.department_id ? deptMap[a.department_id] : undefined,
  })) as CourseAssignment[];
}

export async function createCourseAssignment(
  orgId: string,
  programId: string,
  assignedBy: string,
  userId?: string,
  departmentId?: string,
  dueDate?: string,
): Promise<CourseAssignment | null> {
  const { data, error } = await supabase
    .from("sentinel_course_assignments")
    .insert({
      organization_id: orgId,
      program_id: programId,
      assigned_by: assignedBy,
      user_id: userId ?? null,
      department_id: departmentId ?? null,
      due_date: dueDate ?? null,
    })
    .select("id, organization_id, department_id, user_id, program_id, assigned_by, status, due_date, created_at")
    .single();

  if (error || !data) return null;
  return data as CourseAssignment;
}

export async function updateAssignmentStatus(
  assignmentId: string,
  status: string,
): Promise<boolean> {
  const { error } = await supabase
    .from("sentinel_course_assignments")
    .update({ status })
    .eq("id", assignmentId);
  return !error;
}

export async function deleteCourseAssignment(assignmentId: string): Promise<boolean> {
  const { error } = await supabase
    .from("sentinel_course_assignments")
    .delete()
    .eq("id", assignmentId);
  return !error;
}

export async function updateOrgPortalSettings(
  orgId: string,
  settings: { logo_url?: string; portal_primary_color?: string; portal_tagline?: string },
): Promise<boolean> {
  const { error } = await supabase
    .from("sentinel_organizations")
    .update(settings)
    .eq("id", orgId);
  return !error;
}

export async function transferPortalAdmin(targetUserId: string, demoteSelf: boolean = true): Promise<boolean> {
  const { error } = await supabase.rpc("transfer_portal_admin", {
    p_target_user_id: targetUserId,
    p_demote_self: demoteSelf,
  });
  return !error;
}

export async function assignPortalAdmin(targetUserId: string): Promise<boolean> {
  const { error } = await supabase.rpc("assign_portal_admin", {
    p_target_user_id: targetUserId,
  });
  return !error;
}

export async function logPortalVisit(orgId: string, action: string, email?: string): Promise<void> {
  await supabase.from("sentinel_portal_sessions").insert({
    organization_id: orgId,
    action,
    visitor_email: email ?? null,
  });
}

export async function createDepartment(orgId: string, name: string, description?: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("create_department", {
    p_org_id: orgId,
    p_name: name,
    p_description: description ?? null,
  });
  if (error) return null;
  return data as string;
}

export async function assignDepartmentAdmin(deptId: string, userId: string): Promise<boolean> {
  const { error } = await supabase.rpc("assign_department_admin", {
    p_dept_id: deptId,
    p_user_id: userId,
  });
  return !error;
}

export async function removeEmployee(userId: string): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase.rpc("remove_employee", { p_user_id: userId });
  if (error) return { success: false, error: error.message };
  return { success: true, error: null };
}

export async function createEmployeeInvite(
  email: string,
  departmentId?: string,
  role?: string,
): Promise<string | null> {
  const { data, error } = await supabase.rpc("create_employee_invite", {
    p_email: email,
    p_department_id: departmentId ?? null,
    p_role: role ?? "student",
  });
  if (error) return null;
  return data as string;
}

export async function revokeInvite(inviteId: string): Promise<boolean> {
  const { error } = await supabase.rpc("revoke_invite", { p_invite_id: inviteId });
  return !error;
}

export async function fetchPortalInvites(orgId: string): Promise<PortalInvite[]> {
  const { data, error } = await supabase
    .from("sentinel_invites")
    .select(`
      id, token, email, organization_id, department_id, invited_by,
      role, status, expires_at, created_at
    `)
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false });

  if (error || !data) return [];

  const deptIds = [...new Set(data.map((i) => i.department_id).filter(Boolean))] as string[];
  let deptMap: Record<string, string> = {};
  if (deptIds.length > 0) {
    const { data: depts } = await supabase.from("sentinel_departments").select("id, name").in("id", deptIds);
    if (depts) deptMap = Object.fromEntries(depts.map((d) => [d.id, d.name]));
  }

  const inviterIds = [...new Set(data.map((i) => i.invited_by))] as string[];
  let inviterMap: Record<string, string> = {};
  if (inviterIds.length > 0) {
    const { data: inviters } = await supabase.from("sentinel_users").select("id, name").in("id", inviterIds);
    if (inviters) inviterMap = Object.fromEntries(inviters.map((u) => [u.id, u.name]));
  }

  return data.map((i) => ({
    ...i,
    department_name: i.department_id ? (deptMap[i.department_id] ?? null) : null,
    inviter_name: inviterMap[i.invited_by] ?? null,
  })) as PortalInvite[];
}

export async function assignLicenseToDepartment(licenseId: string, deptId: string): Promise<boolean> {
  const { error } = await supabase.rpc("assign_license_to_department", {
    p_license_id: licenseId,
    p_dept_id: deptId,
  });
  return !error;
}

export async function fetchOrgLicenses(orgId: string): Promise<{ id: string; name: string; type: string; department_id: string | null; seats: number }[]> {
  const { data, error } = await supabase
    .from("sentinel_licenses")
    .select("id, name, type, department_id, seats")
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data as { id: string; name: string; type: string; department_id: string | null; seats: number }[];
}

export async function fetchOrgOrders(orgId: string): Promise<PortalOrder[]> {
  const { data, error } = await supabase
    .from("sentinel_orders")
    .select("id, organization_id, department_id, user_id, order_type, item_name, item_id, quantity, amount, currency, status, receipt_url, invoice_url, created_at")
    .eq("organization_id", orgId)
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data as PortalOrder[];
}

export async function fetchUserOrders(userId: string): Promise<PortalOrder[]> {
  const { data, error } = await supabase
    .from("sentinel_orders")
    .select("id, organization_id, department_id, user_id, order_type, item_name, item_id, quantity, amount, currency, status, receipt_url, invoice_url, created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data as PortalOrder[];
}

export async function updateOrgIndustryPreferences(orgId: string, industries: string[]): Promise<boolean> {
  const { error } = await supabase
    .from("sentinel_organizations")
    .update({ industry_preferences: industries })
    .eq("id", orgId);
  return !error;
}

export async function updateUserIndustryPreferences(userId: string, industries: string[]): Promise<boolean> {
  const { error } = await supabase
    .from("sentinel_users")
    .update({ industry_preferences: industries })
    .eq("id", userId);
  return !error;
}

export async function fetchUserIndustryPrefs(userId: string): Promise<string[] | null> {
  const { data, error } = await supabase
    .from("sentinel_users")
    .select("industry_preferences")
    .eq("id", userId)
    .maybeSingle();
  if (error || !data) return null;
  return (data.industry_preferences as string[]) ?? null;
}

export async function fetchOrgInfo(orgId: string): Promise<{ id: string; name: string; ein: string | null; portal_slug: string | null; industry: string | null; industry_preferences: string[] | null } | null> {
  const { data, error } = await supabase
    .from("sentinel_organizations")
    .select("id, name, ein, portal_slug, industry, industry_preferences")
    .eq("id", orgId)
    .maybeSingle();
  if (error || !data) return null;
  return data as { id: string; name: string; ein: string | null; portal_slug: string | null; industry: string | null; industry_preferences: string[] | null };
}
